import CoreApi from '@/api/CoreApi';
import type {
  CatalogueItem,
  CatalogueItemGroup,
  CatalogueItemDetail,
  ChildItemData,
  ChildItemWithQuantity,
  BomItem,
  PaginatedResponse,
  CatalogueItemPromotionRequest,
  CatalogueItemPromotionRequestSummary,
  PromotionRequestsParams,
  PromotionRequestStatus,
  ErpImportConfig,
  ErpImportResultResponse,
  ErpImportLog,
} from '@/models/Catalogue';
import type {
  CatalogueQueryParams,
  CatalogueFilterClause,
} from '@/models/CatalogueFilters';

export async function getCatalogueItems(
  params?: CatalogueQueryParams,
): Promise<PaginatedResponse<CatalogueItem>> {
  try {
    const queryParams = new URLSearchParams();

    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.filters?.length) {
      queryParams.append('filters', JSON.stringify(params.filters));
    }
    if (params?.sort?.length) {
      queryParams.append('sort', JSON.stringify(params.sort));
    }

    const url = `/catalogue/items${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;
    const response = await CoreApi.axiosClient.get(url);
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to load catalogue items');
  }
}

export interface CatalogueCustomFieldMeta {
  key: string;
  fieldType: 'string' | 'integer' | 'float';
}

export async function getCatalogueItemCustomFields(
  filters?: CatalogueFilterClause[],
): Promise<CatalogueCustomFieldMeta[]> {
  try {
    const queryParams = new URLSearchParams();
    if (filters?.length) {
      queryParams.append('filters', JSON.stringify(filters));
    }
    const url = `/catalogue/items/custom-fields${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;
    const response = await CoreApi.axiosClient.get(url);
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to load catalogue item custom fields');
  }
}

export async function getCatalogueItem(
  uuid: string,
): Promise<CatalogueItemDetail> {
  try {
    const response = await CoreApi.axiosClient.get(`/catalogue/items/${uuid}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to load catalogue item');
  }
}

export async function createCatalogueItem(
  item: CatalogueItem,
): Promise<CatalogueItem> {
  try {
    const response = await CoreApi.axiosClient.post('/catalogue/items', item);
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to create catalogue item');
  }
}

export async function updateCatalogueItem(
  uuid: string,
  item: CatalogueItem,
): Promise<CatalogueItem> {
  try {
    const response = await CoreApi.axiosClient.patch(
      `/catalogue/items/${uuid}`,
      item,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to update catalogue item');
  }
}

export async function deleteCatalogueItem(uuid: string): Promise<void> {
  await CoreApi.axiosClient.delete(`/catalogue/items/${uuid}`);
}

export async function getCatalogueGroups(): Promise<CatalogueItemGroup[]> {
  try {
    const response = await CoreApi.axiosClient.get('/catalogue/groups');
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to load catalogue groups');
  }
}

export async function createCatalogueGroup(
  group: CatalogueItemGroup,
): Promise<CatalogueItemGroup> {
  try {
    const response = await CoreApi.axiosClient.post('/catalogue/groups', group);
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to create catalogue group');
  }
}

export async function updateCatalogueGroup(
  uuid: string,
  group: CatalogueItemGroup,
): Promise<CatalogueItemGroup> {
  try {
    const response = await CoreApi.axiosClient.patch(
      `/catalogue/groups/${uuid}`,
      group,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to update catalogue group');
  }
}

export async function deleteCatalogueGroup(uuid: string): Promise<void> {
  try {
    await CoreApi.axiosClient.delete(`/catalogue/groups/${uuid}`);
  } catch (error) {
    console.error(error);
    throw new Error('Failed to delete catalogue group');
  }
}

export async function getItemChildren(
  uuid: string,
): Promise<ChildItemWithQuantity[]> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/catalogue/items/${uuid}/children`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to load item children');
  }
}

export async function addItemChild(
  parentUuid: string,
  childData: ChildItemData,
): Promise<ChildItemWithQuantity[]> {
  try {
    const response = await CoreApi.axiosClient.post(
      `/catalogue/items/${parentUuid}/children`,
      childData,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to add child item');
  }
}

export async function updateItemChildQuantity(
  parentUuid: string,
  childUuid: string,
  quantity: number,
): Promise<ChildItemWithQuantity[]> {
  try {
    const response = await CoreApi.axiosClient.put(
      `/catalogue/items/${parentUuid}/children/${childUuid}?quantity=${quantity}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to update child item quantity');
  }
}

export async function removeItemChild(
  parentUuid: string,
  childUuid: string,
): Promise<ChildItemWithQuantity[]> {
  try {
    const response = await CoreApi.axiosClient.delete(
      `/catalogue/items/${parentUuid}/children/${childUuid}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to remove child item');
  }
}

export async function uploadItemImage(
  uuid: string,
  file: File,
): Promise<{ images: string[] }> {
  try {
    const formData = new FormData();
    formData.append('image', file);
    const response = await CoreApi.axiosClient.post(
      `/catalogue/items/${uuid}/images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to upload item image');
  }
}

export async function uploadItemFile(
  uuid: string,
  file: File,
): Promise<{ files: string[] }> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await CoreApi.axiosClient.post(
      `/catalogue/items/${uuid}/files`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to upload item file');
  }
}

export async function deleteItemImage(
  uuid: string,
  filename: string,
): Promise<{ images: string[] }> {
  try {
    const response = await CoreApi.axiosClient.delete(
      `/catalogue/items/${uuid}/images/${filename}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to delete item image');
  }
}

export async function deleteItemFile(
  uuid: string,
  filename: string,
): Promise<{ files: string[] }> {
  try {
    const response = await CoreApi.axiosClient.delete(
      `/catalogue/items/${uuid}/files/${filename}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to delete item file');
  }
}

export async function getItemsBom(uuids: string[]): Promise<BomItem[]> {
  try {
    const queryParams = new URLSearchParams();
    uuids.forEach((uuid) => queryParams.append('item_uuids', uuid));

    const url = `/catalogue/items/bom${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;
    const response = await CoreApi.axiosClient.get(url);
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to load items BOM');
  }
}

export async function createItemRevision(uuid: string): Promise<CatalogueItem> {
  const response = await CoreApi.axiosClient.post(
    `/catalogue/items/${uuid}/revision`,
  );
  return response.data;
}

export async function duplicateCatalogueItem(
  uuid: string,
): Promise<CatalogueItem> {
  try {
    const response = await CoreApi.axiosClient.post(
      `/catalogue/items/${uuid}/duplicate`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to duplicate catalogue item');
  }
}

// Promotion Request API

export async function submitPromotionRequest(
  itemUuid: string,
): Promise<CatalogueItemPromotionRequestSummary> {
  const response = await CoreApi.axiosClient.post(
    `/catalogue/items/${itemUuid}/promotion-request`,
  );
  return response.data;
}

export async function getPromotionRequests(
  params?: PromotionRequestsParams,
): Promise<PaginatedResponse<CatalogueItemPromotionRequest>> {
  const queryParams = new URLSearchParams();

  if (params?.page) {
    queryParams.append('page', params.page.toString());
  }
  if (params?.limit) {
    queryParams.append('limit', params.limit.toString());
  }
  if (params?.status) {
    queryParams.append('status', params.status);
  }

  const url = `/catalogue/promotion-requests${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`;
  const response = await CoreApi.axiosClient.get(url);
  return response.data;
}

export async function reviewPromotionRequest(
  requestUuid: string,
  data: { status: PromotionRequestStatus; comment?: string },
): Promise<CatalogueItemPromotionRequest> {
  const response = await CoreApi.axiosClient.patch(
    `/catalogue/promotion-requests/${requestUuid}`,
    data,
  );
  return response.data;
}

export async function cancelPromotionRequest(
  requestUuid: string,
): Promise<void> {
  await CoreApi.axiosClient.delete(
    `/catalogue/promotion-requests/${requestUuid}`,
  );
}

export async function getErpSyncStatus(): Promise<string | null> {
  const response = await CoreApi.axiosClient.get('/catalogue/erp/sync-status');
  return response.data;
}

export async function getErpImportLogs(
  limit: number = 20,
  offset: number = 0,
): Promise<ErpImportLog[]> {
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit.toString());
  queryParams.append('offset', offset.toString());

  const response = await CoreApi.axiosClient.get(
    `/catalogue/erp/import-logs?${queryParams.toString()}`,
  );
  return response.data;
}

export async function importErp(
  file: File,
  config: ErpImportConfig,
  dryRun: boolean = false,
): Promise<ErpImportResultResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('import_config', JSON.stringify(config));

    const url = dryRun
      ? '/catalogue/erp/import?dryRun=true'
      : '/catalogue/erp/import';

    const response = await CoreApi.axiosClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
